import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import moment from "moment";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Card, DatePicker, Empty, Spin, Table, Typography } from "antd";
import "leaflet/dist/leaflet.css";
import { AppStateContext } from "../../context/AppContext";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const defaultCenter = [28.6139, 77.209];

function FitBounds({ locations }) {
  const map = useMap();
  useEffect(() => {
    const points = locations.map((item) => [Number(item.latitude), Number(item.longitude)]);
    if (!points.length) return;
    if (points.length === 1) map.flyTo(points[0], 14);
    else map.flyToBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 14 });
  }, [locations, map]);
  return null;
}

export default function UserNewLocationMapPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const [locations, setLocations] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  setPageHeading("New User Locations");

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    request({
      url: `${apiPath.getUserLocation}/${userId}`,
      method: "GET",
      onSuccess: (response) => setUser(response?.data?.user || null),
      onError: (err) => setError(err?.message || "Failed to load user"),
    });
    request({
      url: `${apiPath.getNewLocations}/${userId}`,
      method: "GET",
      onSuccess: (response) => {
        const list = response?.data?.data || response?.data || [];
        setLocations(Array.isArray(list) ? list : []);
        setLoading(false);
      },
      onError: (err) => {
        setError(err?.message || "Failed to load new locations");
        setLoading(false);
      },
    });
  }, [userId]);

  const dateLocations = useMemo(() => {
    const day = selectedDate.format("YYYY-MM-DD");
    return locations.filter((item) => item.createdAt && moment(item.createdAt).format("YYYY-MM-DD") === day);
  }, [locations, selectedDate]);
  const center = dateLocations.length
    ? [Number(dateLocations[0].latitude), Number(dateLocations[0].longitude)]
    : defaultCenter;

  if (loading) return <div style={{ textAlign: "center", padding: 80 }}><Spin size="large" /></div>;
  if (error) return <Card><Typography.Text type="danger">{error}</Typography.Text></Card>;

  return (
    <div>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/user-management/${userId}/location`)}>
        Back to Location Screen
      </Button>
      <Card
        title={`New Locations${user?.name ? ` — ${user.name}` : ""}`}
        style={{ marginTop: 12 }}
        extra={<DatePicker value={selectedDate} onChange={(date) => setSelectedDate(date || moment())} />}
      >
        {dateLocations.length ? (
          <>
            <div style={{ height: 520, borderRadius: 8, overflow: "hidden" }}>
              <MapContainer center={center} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitBounds locations={dateLocations} />
                {dateLocations.map((item, index) => (
                  <Marker key={item._id || index} position={[Number(item.latitude), Number(item.longitude)]}>
                    <Tooltip permanent direction="top" offset={[0, -24]}>#{index + 1} — {moment(item.createdAt).format("hh:mm A")}</Tooltip>
                    <Popup><strong>{item.name}</strong><div>{item.location || "Location not available"}</div></Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
            <Table
              style={{ marginTop: 16 }}
              size="small"
              rowKey={(item) => item._id}
              dataSource={dateLocations}
              columns={[
                { title: "#", render: (_, __, index) => index + 1, width: 60 },
                { title: "Name", dataIndex: "name" },
                { title: "Location", dataIndex: "location", render: (value) => value || "—" },
                { title: "Date & Time", dataIndex: "createdAt", render: (value) => moment(value).format("DD MMM YYYY, hh:mm A") },
              ]}
              pagination={{ pageSize: 10 }}
            />
          </>
        ) : <Empty description="No new locations found for this date" />}
      </Card>
    </div>
  );
}
